import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {laDateCourte} from '../utils/dates';
import {TopicIcon} from './TopicIcon';
import {
    AppVersions,
    demandLabel,
    FeedbackReport,
    FeedbackStatus,
    FeedbackTag,
    FeedbackTeam,
    PLATFORM_MAP,
    STATUS_COLORS,
    STATUS_LABELS,
    TAG_COLORS,
    TAG_LABELS,
    TEAM_COLORS,
    TEAM_LABELS,
} from '../types/feedback';
import {isVersionOutdated} from '../services/appVersionsService';
import {BulkActionsBar} from './BulkActionsBar';
import {bulkUpdateFeedback} from '../services/feedbackService';
import {useAuth} from '../hooks/useAuth';
import {Eye} from '@phosphor-icons/react';
import {feedbackPath} from '../routes';

interface FeedbackListProps {
    items: FeedbackReport[];
    loading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    appVersions?: AppVersions | null;
    onRefresh?: () => void;
}

export function FeedbackList({items, loading, hasMore, onLoadMore, appVersions, onRefresh}: FeedbackListProps) {
    const t = useTraduire();
    const navigate = useNavigate();
    const {user} = useAuth();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkLoading, setBulkLoading] = useState(false);

    const checkOutdated = (item: FeedbackReport): boolean => {
        if (!appVersions || !item.appVersion) return false;
        const platformKey = PLATFORM_MAP[item.platform];
        if (!platformKey) return false;
        const latestVersion = appVersions[platformKey];
        if (!latestVersion) return false;
        return isVersionOutdated(item.appVersion, latestVersion);
    };

    const toggleSelection = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const toggleAllSelection = () => {
        if (selectedIds.size === items.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(items.map((item) => item.id)));
        }
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
    };

    const handleBulkApply = async (updates: {
        status?: FeedbackStatus;
        team?: FeedbackTeam;
        addTags?: FeedbackTag[];
    }) => {
        if (selectedIds.size === 0) return;

        setBulkLoading(true);
        try {
            await bulkUpdateFeedback(
                Array.from(selectedIds),
                updates,
                user?.email || 'unknown'
            );
            clearSelection();
            onRefresh?.();
        } catch (error) {
            console.error('Bulk update failed:', error);
            alert(t(T.echecMiseAJour));
        } finally {
            setBulkLoading(false);
        }
    };

    if (loading && items.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">{t(T.chargementDesDemandes)}</p>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-600">{t(T.aucuneDemande)}</p>
            </div>
        );
    }

    const allSelected = selectedIds.size === items.length && items.length > 0;
    const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

    return (
        <div>
            {selectedIds.size > 0 && (
                <BulkActionsBar
                    selectedCount={selectedIds.size}
                    onApply={handleBulkApply}
                    onCancel={clearSelection}
                    loading={bulkLoading}
                />
            )}

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(el) => {
                                    if (el) el.indeterminate = someSelected;
                                }}
                                onChange={toggleAllSelection}
                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                            />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t(T.colonneEtat)}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t(T.colonneSujet)}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                            {t(T.colonneEquipe)}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                            {t(T.colonneEtiquettes)}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t(T.colonneMessage)}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                            {t(T.colonnePlateforme)}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t(T.colonneDate)}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t(T.colonneActions)}
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item) => (
                        <tr
                            key={item.id}
                            className={`hover:bg-gray-50 cursor-pointer ${selectedIds.has(item.id) ? 'bg-blue-50' : ''}`}
                            onClick={() => navigate(feedbackPath(item.id))}
                        >
                            <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={selectedIds.has(item.id)}
                                    onChange={(e) => toggleSelection(item.id, e as unknown as React.MouseEvent)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                  <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          STATUS_COLORS[item.status || 'new']
                      }`}
                  >
                    {t(STATUS_LABELS[item.status || 'new'])}
                  </span>
                                    {item.hasUnreadUserReply && (
                                        <span
                                            className="inline-flex items-center justify-center w-2 h-2 bg-red-500 rounded-full"
                                            title={t(T.nouvelleReponseDuClient)}
                                        />
                                    )}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <span className="inline-flex items-center gap-2">
                                    <TopicIcon topic={item.topic} className="text-gray-500 shrink-0"/>
                                    {t(demandLabel(item))}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">
                                {item.team ? (
                                    <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${TEAM_COLORS[item.team] || 'bg-gray-100 text-gray-600'}`}>
                    {t(TEAM_LABELS[item.team]) || item.team}
                  </span>
                                ) : (
                                    <span className="text-gray-400">-</span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-sm hidden lg:table-cell">
                                {item.tags && item.tags.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 max-w-[150px]">
                                        {item.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-600'}`}
                                            >
                        {t(TAG_LABELS[tag]) || tag}
                      </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-gray-400">-</span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                                {item.message?.trim() ? (
                                    <p className="line-clamp-2">{item.message}</p>
                                ) : (
                                    /* Un achat introuvable n'exige plus de message : ce qui
                                       permet d'agir est la methode et la boutique. Sans ce
                                       repli, la ligne s'affichait vide. */
                                    <p className="line-clamp-2 text-gray-500">
                                        {[item.method, item.orderNumber && `#${item.orderNumber}`]
                                            .filter(Boolean)
                                            .join(' · ') || <span className="text-gray-400">-</span>}
                                    </p>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                                <div className="flex flex-col">
                                    <span>{item.platform}</span>
                                    <span
                                        className={`text-xs ${checkOutdated(item) ? 'text-orange-500' : 'text-gray-400'}`}
                                        title={checkOutdated(item) ? t(T.versionPerimee) : undefined}>
                    v{item.appVersion} ({item.buildNumber})
                  </span>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                {item.createdAt?.toDate ? (
                                    <div className="flex flex-col">
                                        <span>{laDateCourte(item.createdAt.toDate())}</span>
                                        <span
                                            className="text-gray-400">{format(item.createdAt.toDate(), 'HH:mm')}</span>
                                    </div>
                                ) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <Link
                                    to={feedbackPath(item.id)}
                                    className="inline-flex items-center justify-center p-1.5 text-primary hover:opacity-60 transition-colors"
                                    title={t(T.voir)}
                                >
                                    <Eye size={22} weight="regular"/>
                                </Link>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                        {items.length} item{items.length !== 1 ? 's' : ''}{hasMore ? '+' : ''}
                    </span>
                    {hasMore && (
                        <button
                            onClick={onLoadMore}
                            disabled={loading}
                            className="py-2 px-4 border border-gray-300 rounded-pill shadow-bo text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 transition-colors"
                        >
                            {loading ? t(T.chargement) : t(T.enVoirPlus)}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
